/**
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops 添付ファイルの入力（ドロップゾーン + ファイル選択）。サイズ・種別・件数上限を ops-core の共有ルールで client 検証し、通過分を onAccept、最初の違反理由を onReject で親へ渡す。詳細ページの添付カードと起票フォームの添付ステージングが同じ入力 UI を共有する
 * @graph-connects none
 */
import { useRef, useState, type DragEvent, type ReactNode } from 'react';
import type { TFunction } from 'i18next';
import {
  formatFileSize,
  isOpsAttachmentMimeAllowed,
  OPS_ATTACHMENT_MAX_BYTES,
  OPS_ATTACHMENT_MAX_COUNT,
} from '@cortex/ops-core';

/** 添付の client 検証の却下理由（i18n キー ops.attachments.errors.* に対応）。 @graph-connects none */
export type OpsAttachmentRejectReason = 'tooLarge' | 'tooMany' | 'badType';

/** client 検証の結果（全件許可、または最初に見つかった却下理由）。 @graph-connects none */
export interface OpsAttachmentValidation {
  /** 全件が上限内なら追加対象のファイル群、1 件でも違反なら空配列。 */
  accepted: File[];
  /** 却下理由（違反なし = null）。 */
  rejection: OpsAttachmentRejectReason | null;
}

/**
 * 追加ファイルを件数・種別・サイズ上限で検証する。上限ルールは @cortex/ops-core を単一 source として
 * API 側と共有する（DRY）。1 件でも違反があれば全体を却下し最初の理由を返す（部分採用しない）。
 * @graph-connects none
 */
export function validateOpsAttachmentFiles(
  files: File[],
  currentCount: number,
): OpsAttachmentValidation {
  if (currentCount + files.length > OPS_ATTACHMENT_MAX_COUNT) {
    return { accepted: [], rejection: 'tooMany' };
  }
  for (const file of files) {
    if (!isOpsAttachmentMimeAllowed(file.type)) {
      return { accepted: [], rejection: 'badType' };
    }
    if (file.size > OPS_ATTACHMENT_MAX_BYTES) {
      return { accepted: [], rejection: 'tooLarge' };
    }
  }
  return { accepted: files, rejection: null };
}

/** OpsAttachmentInput の props。 */
export interface OpsAttachmentInputProps {
  /** 現在の添付数（件数上限チェックの起点）。 */
  currentCount: number;
  /** 検証を通過したファイル群を受け取る（アップロード / ステージングは親が行う）。 */
  onAccept: (files: File[]) => void;
  /** 検証に落ちたときの理由（i18n キー導出に使う）。 */
  onReject: (reason: OpsAttachmentRejectReason) => void;
  /** 翻訳関数。 */
  t: TFunction;
  /** 無効化（アップロード中など）。 */
  disabled?: boolean;
}

/**
 * ドロップゾーン + ファイル選択の入力。ドロップ / 選択されたファイルを client 検証し、通過分を onAccept、
 * 違反時は onReject に理由を渡す。同じファイルを続けて選べるよう選択後に input.value をリセットする。
 * @graph-connects none
 */
export function OpsAttachmentInput({
  currentCount,
  onAccept,
  onReject,
  t,
  disabled = false,
}: OpsAttachmentInputProps): ReactNode {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = (fileList: FileList | null): void => {
    if (fileList === null || fileList.length === 0) return;
    const files = Array.from(fileList);
    const { accepted, rejection } = validateOpsAttachmentFiles(files, currentCount);
    if (rejection !== null) {
      onReject(rejection);
      return;
    }
    onAccept(accepted);
  };

  const onDrop = (e: DragEvent<HTMLLabelElement>): void => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    handleFiles(e.dataTransfer.files);
  };

  const onDragOver = (e: DragEvent<HTMLLabelElement>): void => {
    e.preventDefault();
    if (!disabled) setDragOver(true);
  };

  const hint = t('ops.attachments.dropHint', {
    count: OPS_ATTACHMENT_MAX_COUNT,
    size: formatFileSize(OPS_ATTACHMENT_MAX_BYTES),
  });

  return (
    <label
      className={`flex cursor-pointer flex-col items-center justify-center gap-1 rounded-md border border-dashed px-3 py-4 text-center text-xs transition-colors ${
        dragOver
          ? 'border-[var(--color-accent-primary)] bg-[var(--color-bg-hover)]'
          : 'border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]'
      } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={() => {
        setDragOver(false);
      }}
    >
      <span aria-hidden="true" className="text-base text-[var(--color-text-muted)]">
        ＋
      </span>
      <span className="font-semibold text-[var(--color-accent-primary)]">
        {t('ops.attachments.choose')}
      </span>
      <span className="text-[var(--color-text-muted)]">{hint}</span>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="sr-only"
        aria-label={t('ops.attachments.addLabel')}
        disabled={disabled}
        onChange={(e) => {
          handleFiles(e.target.files);
          // 同じファイルを続けて選び直せるように value をクリアする（change を再発火させる）。
          if (inputRef.current !== null) inputRef.current.value = '';
        }}
      />
    </label>
  );
}
